/**
 * ArtMap — canonical asset/ID mapping.
 *
 * Maps building and unit data IDs to art-manifest assets and, from there, to
 * loadable web model paths. Render code resolves models through here by ID —
 * never by hardcoded paths.
 *
 * Source of truth for *what exists* is `art_manifest.json` (mirrored from the
 * main repo; read-only here). Source of truth for *what is vendored for the
 * web* is `public/assets/manifest.json` (pinned, versioned GLB exports per
 * AGENTS.md; empty until the Phase 0 conversion lands).
 *
 * PROTOTYPE MAPPING (web -> UE): the manifest's `unreal_asset_path` /
 * `export_paths.unreal_candidate` identify the UE-side asset; `webPath` is the
 * conventional location of the decimated web export. `sourcePath` records the
 * Unreal FBX export the web GLB was converted from, for the asset audit trail.
 * (Godot was retired; no Godot paths are used anywhere in this pipeline.)
 */
import type { BuildingDef, UnitDef } from "./types";
import CONVENTION from "./webpath_convention.json";

/**
 * Web-path convention (single source of truth: webpath_convention.json,
 * shared with tools/vendor_models.py and pinned by tests/webpaths.test.ts).
 * Render code and the vendor tool must both go through these helpers —
 * never retype the pattern.
 */
export function buildingWebPath(canonicalId: string, band: string): string {
  return CONVENTION.buildings
    .replace("{canonical_id}", canonicalId)
    .replace("{band}", band);
}

export function unitWebPath(canonicalId: string): string {
  return CONVENTION.units.replace("{canonical_id}", canonicalId);
}

/** LOD-tier web path for a building band (1..3, art-pipeline LOD tiers). */
export function buildingLodWebPath(canonicalId: string, band: string, lod: number): string {
  return CONVENTION.buildings_lod
    .replace("{canonical_id}", canonicalId)
    .replace("{band}", band)
    .replace("{lod}", String(lod));
}

/** LOD-tier web path for a unit (1..3, art-pipeline LOD tiers). */
export function unitLodWebPath(canonicalId: string, lod: number): string {
  return CONVENTION.units_lod
    .replace("{canonical_id}", canonicalId)
    .replace("{lod}", String(lod));
}

/** Highest-numbered vendored LOD tier (1..3), or 0 when none is vendored. */
export function highestVendoredLod(vendored: ReadonlySet<string>, lodPaths: string[]): number {
  let best = 0;
  for (let i = 0; i < lodPaths.length; i++) {
    if (vendored.has(lodPaths[i])) best = i + 1;
  }
  return best;
}

/** Manifest `dimensions_and_origin` block, when the asset declares one. */
export interface AssetDimensions {
  /** Model-space size in metres [x, y, z], or null. */
  sizeM: [number, number, number] | null;
  /** Intended gameplay footprint in metres [w, d], or null. */
  footprintM: [number, number] | null;
  /** Origin rule, e.g. "ground center", or null. */
  originRule: string | null;
  /** Forward axis in the source file (e.g. "-Y" blender). */
  forwardAxis: string | null;
}

/** One entry from art_manifest.json. */
export interface ArtAsset {
  canonicalId: string;
  assetType: string;
  exportPaths: Record<string, string | null>;
  sourceVersion: string | null;
  unrealAssetPath: string | null;
  /** Skeletal animation action names (e.g. AN_faber_hammer_high). */
  animations: string[];
  /** Manifest dimensions block; null when the asset declares none. */
  dimensions: AssetDimensions | null;
}

/** A data ID resolved to a web-loadable model (or to "unmapped"). */
export interface ResolvedModel {
  /** Manifest canonical id, or null when the data ID has no mapped asset. */
  canonicalId: string | null;
  /** Echelon band selected (buildings only), or null. */
  band: string | null;
  /** Conventional web path, e.g. `assets/buildings/forum_band1.glb`. Null when unmapped. */
  webPath: string | null;
  /** True only when the GLB is actually vendored under public/assets/. */
  available: boolean;
  /**
   * Vendored LOD-tier web paths, tiers 1..3 in order (art-pipeline LODs).
   * Only tiers actually present under public/assets/ are listed. The game
   * uses the highest-numbered tier as far-LOD geometry.
   */
  lodPaths: string[];
  /** Manifest export path the web GLB was (or will be) converted from. */
  sourcePath: string | null;
  /** Animation action names for skeletal assets. */
  animations: string[];
  /** Manifest dimensions block; null when the asset declares none. */
  dimensions: AssetDimensions | null;
}

/** Echelon bands in upgrade order. Band "1" covers Echelons 1+2 (shared model). */
const BAND_ORDER = ["1", "3", "4", "5"];

/**
 * Resolve which model band a building shows at a faction echelon.
 * E1/E2 -> band "1"; E3 -> "3"; E4 -> "4"; E5 -> "5".
 * Falls back to the nearest lower defined band (so a building with only
 * band "1" keeps its look all game), then to the lowest defined band.
 */
export function resolveEchelonBand(
  bands: Record<string, string>,
  echelon: number,
): string | null {
  const defined = BAND_ORDER.filter((b) => bands[b] !== undefined);
  if (defined.length === 0) {
    const keys = Object.keys(bands);
    return keys.length > 0 ? keys[0] : null;
  }
  const e = Math.min(Math.max(Math.round(echelon), 1), 5);
  const target = e <= 2 ? "1" : String(e);
  const idx = BAND_ORDER.indexOf(target);
  for (let i = idx; i >= 0; i--) {
    if (defined.includes(BAND_ORDER[i])) return BAND_ORDER[i];
  }
  return defined[0];
}

/** Building-id -> manifest canonical id. Direct match today; Gaul/Samnite and
 *  un-authored buildings (temple, orchard, ...) have no asset yet -> null. */
function buildingCanonicalId(assets: Map<string, ArtAsset>, buildingId: string): string | null {
  return assets.has(buildingId) ? buildingId : null;
}

/** Unit-id -> manifest canonical id: direct match, else the `<id>_kit`
 *  modular-kit convention (signifer -> signifer_kit). No fuzzy matching. */
function unitCanonicalId(assets: Map<string, ArtAsset>, unitId: string): string | null {
  if (assets.has(unitId)) return unitId;
  const kit = `${unitId}_kit`;
  return assets.has(kit) ? kit : null;
}

const UNMAPPED: ResolvedModel = {  canonicalId: null,
  band: null,
  webPath: null,
  available: false,
  lodPaths: [],
  sourcePath: null,
  animations: [],
  dimensions: null,
};

/** Fresh placeholder descriptor for ids with no mapped asset. */
export function unmappedModel(): ResolvedModel {
  return { ...UNMAPPED, animations: [] };
}

/**
 * Stable visual key for a resolved model: `<canonical_id>@band<N>`, or
 * `placeholder:<data id>` when unmapped. Views store this; a change means
 * the visual must be rebuilt (e.g. echelon-band upgrade).
 */
export function artKeyFor(model: ResolvedModel, dataId: string): string {
  return model.canonicalId ? `${model.canonicalId}@band${model.band}` : `placeholder:${dataId}`;
}

/** Parse the manifest `dimensions_and_origin` block; null when absent. */
function parseDimensions(doc: unknown): AssetDimensions | null {
  if (!doc || typeof doc !== "object") return null;
  const d = doc as Record<string, unknown>;
  const num3 = (v: unknown): [number, number, number] | null =>
    Array.isArray(v) && v.length >= 3 && v.slice(0, 3).every((n) => typeof n === "number")
      ? [v[0] as number, v[1] as number, v[2] as number]
      : null;
  const num2 = (v: unknown): [number, number] | null =>
    Array.isArray(v) && v.length >= 2 && v.slice(0, 2).every((n) => typeof n === "number")
      ? [v[0] as number, v[1] as number]
      : null;
  const sizeM = num3(d["dimensions_m"]);
  const footprintM = num2(d["gameplay_footprint_m"]);
  const originRule = typeof d["origin_rule"] === "string" ? d["origin_rule"] : null;
  const forwardAxis =
    typeof d["forward_axis_blender"] === "string" ? d["forward_axis_blender"] : null;
  if (!sizeM && !footprintM && !originRule && !forwardAxis) return null;
  return { sizeM, footprintM, originRule, forwardAxis };
}

export class ArtMap {
  assets = new Map<string, ArtAsset>();
  /** Web paths (e.g. `assets/buildings/forum_band1.glb`) vendored under public/assets/. */
  vendored = new Set<string>();

  static fromDocs(manifestDoc: unknown, vendoredDoc: unknown): ArtMap {
    const map = new ArtMap();
    const list = Array.isArray(manifestDoc)
      ? manifestDoc
      : (manifestDoc as Record<string, unknown> | null)?.["assets"];
    const arr = Array.isArray(list) ? list : [];
    for (const r of arr) {
      const d = r as Record<string, unknown>;
      const id = d["canonical_id"];
      if (typeof id !== "string") continue;
      const skel = (d["skeleton_animation"] as Record<string, unknown> | undefined) ?? {};
      const anims = Array.isArray(skel["animation_set"])
        ? (skel["animation_set"] as unknown[]).filter((a): a is string => typeof a === "string")
        : [];
      map.assets.set(id, {
        canonicalId: id,
        assetType: typeof d["asset_type"] === "string" ? d["asset_type"] : "unknown",
        exportPaths:
          d["export_paths"] && typeof d["export_paths"] === "object"
            ? (d["export_paths"] as Record<string, string | null>)
            : {},
        sourceVersion: typeof d["source_version"] === "string" ? d["source_version"] : null,
        unrealAssetPath:
          typeof d["unreal_asset_path"] === "string" ? d["unreal_asset_path"] : null,
        animations: anims,
        dimensions: parseDimensions(d["dimensions_and_origin"]),
      });
    }
    const files = (vendoredDoc as Record<string, unknown> | null)?.["files"];
    if (Array.isArray(files)) {
      for (const f of files) if (typeof f === "string") map.vendored.add(f);
    }
    return map;
  }

  asset(id: string): ArtAsset | undefined {
    return this.assets.get(id);
  }

  /** Resolve a building to its model at a faction echelon. */
  buildingModel(def: BuildingDef, echelon: number): ResolvedModel {
    const canonicalId = buildingCanonicalId(this.assets, def.id);
    if (!canonicalId) return { ...UNMAPPED };
    const asset = this.assets.get(canonicalId)!;
    const bands = (def.extra["model_paths_by_echelon_band"] as Record<string, string>) ?? {};
    const band = resolveEchelonBand(bands, echelon);
    if (!band) return { ...UNMAPPED, canonicalId, dimensions: asset.dimensions };
    const webPath = buildingWebPath(canonicalId, band);
    const lodPaths = [1, 2, 3]
      .map((lod) => buildingLodWebPath(canonicalId, band, lod))
      .filter((p) => this.vendored.has(p));
    return {
      canonicalId,
      band,
      webPath,
      available: this.vendored.has(webPath),
      lodPaths,
      sourcePath: asset.exportPaths["unreal_candidate"] ?? null,
      animations: asset.animations,
      dimensions: asset.dimensions,
    };
  }

  /** Resolve a unit to its model. */
  unitModel(def: UnitDef): ResolvedModel {
    const canonicalId = unitCanonicalId(this.assets, def.id);
    if (!canonicalId) return { ...UNMAPPED };
    const asset = this.assets.get(canonicalId)!;
    const webPath = unitWebPath(canonicalId);
    const lodPaths = [1, 2, 3]
      .map((lod) => unitLodWebPath(canonicalId, lod))
      .filter((p) => this.vendored.has(p));
    return {
      canonicalId,
      band: null,
      webPath,
      available: this.vendored.has(webPath),
      lodPaths,
      sourcePath: asset.exportPaths["unreal_candidate"] ?? null,
      animations: asset.animations,
      dimensions: asset.dimensions,
    };
  }
}
